import { useEffect, useRef, useState } from "react";
import { Mail, Lock } from "lucide-react";
import {
  Box,
  Grid,
  Typography,
  Button,
  CircularProgress,
  InputLabel
} from "@mui/material";
import { LoginStyles } from "../Styles/loginStyles";
import { useTheme } from "@mui/material/styles";
import { TextFieldWithIcon } from "../Componets/ui/TextFieldIcon";
import SignUp from "./signUp";
import { MotionBox } from "../Componets/ui/MotionBox";
import logo from "../Assests/Images/logo.png";
import { AuthServices } from "../Services/authServices";
import {
  validateEmpty,
  validateMinLength,
  validateEmail,
} from "@shared/Validations/validations";
import { useNavigate } from "react-router-dom";
import { apiHandler, LoaderFun, ErrLoaderOff } from "@shared/Functions/apiHandler";
import VerifyModal from "./otpVerify";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import ReCAPTCHA from "react-google-recaptcha";
import { ApiConfig } from "@shared/Config/config";
import { useParams } from "react-router-dom";
import { useDrawerOpenFun } from "../Context/modelContext";

export default function Login() {
  const theme = useTheme();
  const navigate = useNavigate();
  const classes = LoginStyles(theme);
  const { deleteStatus } = useParams();
  const { loginModal, setLoginModal } = useDrawerOpenFun();

  if (deleteStatus) {
    sessionStorage.setItem("deleteStatus", true);
  }

  const [isSignUp, setIsSignUp] = useState(0);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signUpData, setSignUpData] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState({ email: "", password: "", captcha: "" });
  const [verifyOtpStatus, setVerifyOtpStatus] = useState({
    email: "",
    phone: "",
    emailStatus: "",
    phoneStatus: "",
  });
  const [EmailOtp, setEmailOtp] = useState(false);
  const [NumberOtp, setNumberOtp] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccpt, setTermsAccpt] = useState(false);
  const [fingerprint, setFingerprint] = useState("");

  // recaptcha (login)
  const recaptchaLoginRef = useRef(null);
  const [loginCaptchaToken, setLoginCaptchaToken] = useState("");

  const handleLoginInputChange = (field) => (e) =>
    setLoginData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSignUpInputChange = (field) => (e) => {
    const val = e.target.value;
    setSignUpData((prev) => ({ ...prev, [field]: val }));
  };

  // FingerprintJS
  const FingerprintJSdata = async () => {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setFingerprint(result?.visitorId || "");
    } catch (err) {
      console.warn("FingerprintJS load failed:", err);
      setFingerprint("");
    }
  };

  useEffect(() => {
    sessionStorage.clear();
    FingerprintJSdata();
  }, []);

  // recaptcha login handlers
  const onLoginRecaptchaChange = async (token) => {
    setIsLoading(true);
    const payload = { token };
    const res = await AuthServices.verifyRecaptcha(payload);
    apiHandler(res, classes.successToast, true);
    LoaderFun(res, setIsLoading);
    ErrLoaderOff(res, setIsLoading);

    if (res?.status === 200) {
      setLoginCaptchaToken(token);    // token stored in local state
      setError((prev) => ({ ...prev, captcha: "" }));
    } else {
      recaptchaLoginRef.current.reset();
      setError((prev) => ({ ...prev, captcha: "verification failed, Please try again" }));
    }
  };


  const onLoginRecaptchaExpired = () => {
    setLoginCaptchaToken("");
    setError((prev) => ({ ...prev, captcha: "Captcha expired. Please complete again." }));
  };

  // ---------------- Login ----------------
  const handleLoginFunc = (e) => {
    e.preventDefault();

    const errObj = {
      email: validateEmpty(loginData.email, "Email"),
      password:
        validateEmpty(loginData.password, "Password") ||
        validateMinLength(loginData.password, "Password", 4),
      captcha: !loginCaptchaToken ? "Please complete the captcha." : "",
    };
    setError(errObj);
    const hasErrors = Object.values(errObj).some((v) => v);
    if (hasErrors) return;

    loginAPI();
  };

  const loginAPI = async () => {
    setIsLoading(true);
    try {
      const payload = {
        emailOrPhone: loginData.email,
        password: loginData.password,
        platform: 3,
        deviceId: fingerprint,
        uniqueId: fingerprint,
      };

      const res = await AuthServices.login(payload);
      apiHandler(res, classes.successToast, true);

      if (res?.status === 200) {
        const data = res?.data?.data;
        // store tokens and user info
        sessionStorage.setItem("refreshToken", data.refreshToken);
        sessionStorage.setItem("token", data.accessToken);
        sessionStorage.setItem("email", data.email);
        sessionStorage.setItem("phoneNo", data.phoneNo);

        setVerifyOtpStatus({
          email: data.email,
          phone: data.phoneNo,
          emailStatus: data.emailVerify,
          phoneStatus: data.phoneVerify,
        });

        if (!data.phoneVerify) setNumberOtp(true);
        else if (!data.emailVerify) setEmailOtp(true);
        else if (loginModal) setLoginModal(false);
        else navigate("/home");
      }
    } catch (err) {
      console.error("loginAPI error", err);
    } finally {
      setIsLoading(false);
      // reset recaptcha visually so user can retry
      try {
        if (recaptchaLoginRef.current && typeof recaptchaLoginRef.current.reset === "function")
          recaptchaLoginRef.current.reset();
      } catch (e) { }
      setLoginCaptchaToken("");
    }
  };

  // ---------------- SignUp handler (receives token from SignUp child) ----------------
  const handleSignUpFunc = (e, captchaToken) => {
    e.preventDefault();
    // basic validation
    const err = {
      email:
        validateEmpty(signUpData.email, "Email") ||
        validateEmail(signUpData.email, "Email"),
      password:
        validateEmpty(signUpData.password, "Password") ||
        validateMinLength(signUpData.password, "Password", 4),
      fullName:
        validateEmpty(signUpData.fullName, "Full Name") ||
        validateMinLength(signUpData.fullName, "Full Name", 4),
      phoneNumber:
        validateEmpty(signUpData.phoneNumber, "Phone Number") ||
        validateMinLength(signUpData.phoneNumber, "Phone Number", 4),
      confirmPassword:
        signUpData.password !== signUpData.confirmPassword
          ? "Passwords do not match"
          : "",
      captcha: !captchaToken ? "Please complete the captcha." : "",
    };

    setError(err);
    const hasErrors = Object.values(err).some((v) => v);
    if (hasErrors) return;

    signUpAPI();
  };

  const signUpAPI = async () => {
    if(!termsAccpt) {
      alert("Please accept the terms and conditions");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: signUpData.fullName,
        phone: signUpData.phoneNumber,
        email: signUpData.email,
        password: signUpData.password,
        platform: 3,
        marketingOptIn: marketingConsent,
        deviceId: fingerprint,
        uniqueId: fingerprint,
      };

      const res = await AuthServices.register(payload);
      apiHandler(res, classes.successToast, true);

      if (res?.status === 200) {
        const data = res?.data?.data;
        sessionStorage.setItem("refreshToken", data.refreshToken);
        sessionStorage.setItem("token", data.accessToken);
        sessionStorage.setItem("email", signUpData.email);
        sessionStorage.setItem("phoneNo", signUpData.phoneNumber);

        setVerifyOtpStatus({
          email: signUpData.email,
          phone: signUpData.phoneNumber,
          emailStatus: false,
          phoneStatus: false,
        });
        setNumberOtp(true);
      }
    } catch (err) {
      console.error("signUpAPI error", err);
    } finally {
      setIsLoading(false);
    }
  };

  // OTP verify functions
  const verifyEmail = async (code) => {
    const payload = {
      otp: code,
      emailOrPhone: verifyOtpStatus?.email,
      type: 1,
      screenType: 1,
    };
    const res = await AuthServices.verifyOtp(payload);
    apiHandler(res, classes.successToast, true);
    if (res?.status === 200) {
      setVerifyOtpStatus((prev) => ({ ...prev, emailStatus: true }));
      setEmailOtp(false);
      if (!verifyOtpStatus.phoneStatus) setNumberOtp(true);
      else if (loginModal) setLoginModal(false);
      else navigate("/home");
    }
  };

  const verifyMobileNumber = async (code) => {
    const payload = {
      otp: code,
      emailOrPhone: verifyOtpStatus?.phone,
      type: 2,
      screenType: 1,
    };
    const res = await AuthServices.verifyOtp(payload);
    apiHandler(res, classes.successToast, true);
    if (res?.status === 200) {
      setVerifyOtpStatus((prev) => ({ ...prev, phoneStatus: true }));
      setNumberOtp(false);
      if (!verifyOtpStatus.emailStatus) setEmailOtp(true);
      else if (loginModal) setLoginModal(false);
      else navigate("/home");
    }
  };

  return (
    <>
      <VerifyModal
        open={EmailOtp}
        onClose={() => setEmailOtp(false)}
        email={verifyOtpStatus?.email}
        setData={setVerifyOtpStatus}
        onVerify={verifyEmail}
        Status={1}
      />

      <VerifyModal
        open={NumberOtp}
        onClose={() => setNumberOtp(false)}
        email={verifyOtpStatus?.phone}
        setData={setVerifyOtpStatus}
        onVerify={verifyMobileNumber}
        Status="2"
      />

      <Grid container sx={classes.mainDiv}>
        {/* Left Hero */}
        <Grid size={{ xs: 0, sm: 0, md: 7, lg: 7 }} sx={classes.rootBox}>
          <Box sx={classes.bgDiv} />
          <Box zIndex={2}>
            <MotionBox delay={0.4} textAlign="right" mt={1}>
              <Typography sx={classes.heading}>
                Fuel Delivered to Your Doorstep
              </Typography>
              <Typography sx={classes.subHeading}>
                Skip the gas station. Get premium fuel delivered wherever you
                are, whenever you need it.
              </Typography>
            </MotionBox>
          </Box>
        </Grid>

        {/* Right Auth Form */}
        <Grid size={{ xs: 12, sm: 12, md: 5, lg: 5 }} sx={classes.authDiv}>
          <Box sx={classes.authDiv2}>
            <img src={logo} alt="logo" style={classes.logoImg} />
            <Typography sx={classes.welcomeText}>
              {isSignUp === 1 ? "Welcome!" : "Welcome Back!"}
            </Typography>

            <MotionBox>
              <Box sx={classes.tabMainDiv}>
                <Button
                  onClick={() => setIsSignUp(0)}
                  sx={isSignUp === 0 ? classes.tabActive : classes.tabInActive}
                >
                  Login
                </Button>

                <Button
                  onClick={() => setIsSignUp(1)}
                  sx={isSignUp === 1 ? classes.tabActive : classes.tabInActive}
                >
                  Sign Up
                </Button>
              </Box>
            </MotionBox>

            {/* Login Form */}
            {!isSignUp && (
              <Box component="form" onSubmit={handleLoginFunc}>
                <Box mt={3}>
                  <TextFieldWithIcon
                    value={loginData.email}
                    handleLoginInputChange={handleLoginInputChange}
                    placeholder="Enter Your Email / Phone Number"
                    icon={<Mail style={classes.inputIcon} />}
                    title="email"
                    delay={0.1}
                    mt={3}
                    error={error.email}
                  />
                </Box>

                <TextFieldWithIcon
                  value={loginData.password}
                  handleLoginInputChange={handleLoginInputChange}
                  placeholder="Enter Your Password"
                  icon={<Lock style={classes.inputIcon} />}
                  title="password"
                  delay={0.2}
                  mt={1}
                  type="password"
                  error={error.password}
                />

                <Box sx={{ ...classes.flex_end, mt: 1 }}>
                  <InputLabel
                    onClick={() => { navigate("/forgot-password") }}
                    sx={{ cursor: "pointer" }}
                  >
                    Forgot Password
                  </InputLabel>
                </Box>

                <MotionBox delay={0.4}>
                  <Box mt={2}>

                    <ReCAPTCHA
                      ref={recaptchaLoginRef}
                      sitekey={ApiConfig?.recaptchaKey}
                      onChange={onLoginRecaptchaChange}
                      onExpired={onLoginRecaptchaExpired}
                    />

                    <Typography variant="caption" color="error" sx={classes.errorText}>
                      {error?.captcha}
                    </Typography>

                  </Box>
                </MotionBox>

                <MotionBox delay={0.5}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={classes.submitBtn}
                    disabled={isLoading || !loginCaptchaToken}
                  >
                    {isLoading ? <CircularProgress size={24} /> : "Login"}
                  </Button>

                  <Typography sx={classes.versionText}>
                    {ApiConfig.ver_user}
                  </Typography>
                </MotionBox>
              </Box>
            )}

            {/* Sign Up Form */}
            {isSignUp === 1 && (
              <SignUp
                isLoading={isLoading}
                handleSignUpInputChange={handleSignUpInputChange}
                signUpData={signUpData}
                handleSignUpFunc={handleSignUpFunc} // will be called as (e, captchaToken)
                error={error}
                marketingConsent={marketingConsent}
                setMarketingConsent={setMarketingConsent}
                termsAccpt={termsAccpt}
                setTermsAccpt={setTermsAccpt}
              />
            )}
          </Box>
        </Grid>

      </Grid>

    </>
  );
}
